import * as PIXI from 'pixi.js'

type ViewportConfig = {
  isPanEnabled?: boolean
  isPinchZoomEnabled?: boolean
  isWheelZoomEnabled?: boolean
  onScaleChange?: (scale: number) => void
}

interface ViewportInstance extends PIXI.Container {
  unbind: () => void
}

export const Viewport = (app: PIXI.Application, config?: ViewportConfig): ViewportInstance => {
  const baseContainer = new PIXI.Container()
  baseContainer.x = app.screen.width / 2
  baseContainer.y = app.screen.height / 2

  if (!config) return Object.assign(baseContainer, { unbind: () => { } })
  const { isPanEnabled, isPinchZoomEnabled, isWheelZoomEnabled, onScaleChange } = config

  baseContainer.interactive = true

  let dragging = false
  let dragData: PIXI.FederatedPointerEvent | null = null

  function onDragStart(event: PIXI.FederatedPointerEvent) {
    dragging = true
    dragData = event
    baseContainer.cursor = 'grabbing'
  }

  function onDragMove(e: PIXI.FederatedPointerEvent) {
    if (!dragging || !dragData) return
    baseContainer.x += e.movementX
    baseContainer.y += e.movementY
  }

  function onDragEnd() {
    dragging = false
    dragData = null
    baseContainer.cursor = 'grab'
  }

  if (!(app.view instanceof HTMLCanvasElement)) {
    throw new Error('app.view must be an HTMLCanvasElement for viewport functionality.')
  }
  const canvasView: HTMLCanvasElement = app.view

  const wheelZoom = (e: WheelEvent) => {
    e.preventDefault()
    const rect = canvasView.getBoundingClientRect()
    const pointer = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
    const before = baseContainer.toLocal(pointer, app.stage)
    const scaleFactor = e.deltaY < 0 ? 1.1 : 1 / 1.1
    baseContainer.scale.x *= scaleFactor
    baseContainer.scale.y *= scaleFactor
    onScaleChange?.(baseContainer.scale.x)
    const after = baseContainer.toLocal(pointer, app.stage)
    baseContainer.x += (after.x - before.x) * baseContainer.scale.x
    baseContainer.y += (after.y - before.y) * baseContainer.scale.y
  }

  let pinchStartDist = 0
  let pinchStartScale = 1
  const touchStart = (e: TouchEvent) => {
    if (e.touches.length !== 2) return
    e.preventDefault()
    const t1 = e.touches[0]
    const t2 = e.touches[1]
    if (!t1 || !t2) {
      return
    }
    pinchStartDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
    pinchStartScale = baseContainer.scale.x
  }

  const touchMove = (e: TouchEvent) => {
    if (e.touches.length !== 2) return
    e.preventDefault()
    const t1 = e.touches[0]
    const t2 = e.touches[1]
    if (!t1 || !t2) {
      return
    }
    const rect = canvasView.getBoundingClientRect()
    const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
    const scaleFactor = dist / pinchStartDist

    const midpoint = {
      x: (t1.clientX + t2.clientX) / 2 - rect.left,
      y: (t1.clientY + t2.clientY) / 2 - rect.top,
    }
    const before = baseContainer.toLocal(midpoint, app.stage)
    baseContainer.scale.x = pinchStartScale * scaleFactor
    baseContainer.scale.y = pinchStartScale * scaleFactor
    onScaleChange?.(baseContainer.scale.x)
    const after = baseContainer.toLocal(midpoint, app.stage)
    baseContainer.x += (after.x - before.x) * baseContainer.scale.x
    baseContainer.y += (after.y - before.y) * baseContainer.scale.y
  }

  if (!!isPanEnabled) {
    baseContainer.cursor = 'grab'
    baseContainer
      .on('pointerdown', onDragStart)
      .on('pointermove', onDragMove)
      .on('pointerup', onDragEnd)
      .on('pointerupoutside', onDragEnd)
  }

  if (!!isWheelZoomEnabled) {
    canvasView.addEventListener('wheel', wheelZoom)
  }

  if (!!isPinchZoomEnabled) {
    canvasView.addEventListener('touchstart', touchStart)
    canvasView.addEventListener('touchmove', touchMove)
  }

  const unbindImplementation = () => {
    if (!!isPanEnabled) {
      baseContainer.off('pointerdown', onDragStart)
      baseContainer.off('pointermove', onDragMove)
      baseContainer.off('pointerup', onDragEnd)
      baseContainer.off('pointerupoutside', onDragEnd)
    }

    if (!!isWheelZoomEnabled) {
      canvasView.removeEventListener('wheel', wheelZoom)
    }

    if (!!isPinchZoomEnabled) {
      canvasView.removeEventListener('touchstart', touchStart)
      canvasView.removeEventListener('touchmove', touchMove)
    }
  }

  const viewportInstance = Object.assign(baseContainer, { unbind: unbindImplementation })

  return viewportInstance
}
